import redoIcon from "@assets/icons/redo.svg";
import saveIcon from "@assets/icons/saveTransparent.svg";
import undoIcon from "@assets/icons/undo.svg";
import lockIcon from "@assets/icons/lock.svg";
import unlockIcon from "@assets/icons/unlock.svg";
import { SHOW_ELECTRIC_RELOAD_BUTTON } from '@components/../config';
import Icon from '@components/global/new/Icon/Icon';
import { useEditorState3 } from "@mobx/hooks/useEditorState3";
import { usePlaceEditorsState3 } from "@mobx/hooks/usePlacedEditorState3";
import connectionsStore, { NodeFigure3, NodeType3, PlaceID3 } from "@mobx/store/connections";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import cn from "classnames";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { CSSProperties, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactFlow, {
  Background,
  ControlButton,
  Connection,
  Controls,
  Edge,
  MiniMap,
  NodeChange,
  OnConnect,
  OnNodesChange,
  XYPosition,
  useReactFlow,
  useViewport
} from 'reactflow';
import 'reactflow/dist/style.css';
import RefreshRacks3 from "../RefreshRacks3";
import styles from './NetworkEditor.module.scss';
import { Figure3 } from './NodeIcon';
import {
  //BuildingNode,
  CellNode3,
  CircleNode3,
  ConsumerNode3,
  CounterNode3,
  DiamondNode3,
  ShieldNode3,
  SquareNode3,
  TransformerNode3,
  TriangleNode3,
  nodesDescriptions3,
} from './nodes';
import { WLP202603 } from "../../../config";

const NodeTypes = {
  cell: CellNode3,
  transformer: TransformerNode3,
  counter: CounterNode3,
  shield: ShieldNode3,
  //building: BuildingNode,
  consumer: ConsumerNode3,
  square: SquareNode3,
  triangle: TriangleNode3,
  circle: CircleNode3,
  diamond: DiamondNode3,
};

const useControl = (props: NetworkEditorProps3) => {
  const { 
    nodes = [], edges, saved, loading, forth,
    undoLength = 0, redoLength = 0, locked, savedViewport,
  } = useEditorState3(props.kind, props.placeID, (editor, building) => ({
    nodes: editor?.data.nodes,
    edges: editor?.data.edges,
    saved: building?.saved,
    loading: editor?.loading,
    undoLength: editor?.undoLength,
    redoLength: editor?.redoLength,
    forth: editor?.data.tree.forth,
    locked: editor?.locked,
    savedViewport: editor?.viewport,
  })) ?? {};
  const { data: dataToSave } = usePlaceEditorsState3(props.placeID) ?? {};
  const editorRef = props.editorRef;
  const [isInitialViewport, setIsInitialViewport] = useState(true);
  const [dropLockedHintStyle, setDropLockedHintStyle] = useState<CSSProperties>();
  const internalViewport = useViewport();
  const reactFlow = useReactFlow();
  const placeStore = useMemo(() => props.placeID ? (connectionsStore.getPlaceStore(props.placeID)) : undefined, [props.placeID]);
  const dragStore = placeStore?.drag;
  const highlightedNodeIDsSign = useMemo(() => props.highlightNodeIDs?.join(",") ?? "", [props.highlightNodeIDs]);
  const renderedNodes = useMemo(() =>
    nodes?.map((n) =>
      toJS(
        props.highlightNodeIDs?.includes(n.id)
          ? { ...n, data: { ...n.data, highlighted: true } }
          : n
      )
    ) ?? [],
  [highlightedNodeIDsSign, nodes, props.highlightNodeIDs]);

  //useEffect(() => dbg.log("TREE", forth), [forth]);
  //useEffect(() => dbg.log("VIEWPORT", internalViewport), [internalViewport]);
  useEffect(() => {
    if (isInitialViewport) setIsInitialViewport(false);
    placeStore?.saveEditorViewport(internalViewport);
  }, [internalViewport])
  
  const onConnect: OnConnect = useCallback(
    (params: Edge<any> | Connection) => placeStore?.connect(params),
    [],
  );

  const onNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => placeStore?.applyNodeChanges(changes),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => placeStore?.applyEdgeChanges(changes),
    []
  );

  const onClear = () => placeStore?.clear();

  const onDragStart = (event: React.DragEvent, NodeType3: NodeType3) => {
    event.dataTransfer.setData('application/reactflow', NodeType3);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();

    const reactFlowBounds = event.currentTarget.getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow') as NodeType3;
    if (!type) return;

    const position: XYPosition = {
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    };

    placeStore?.newNode(type, position);
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onUndoRedo = (v: boolean) => placeStore?.applyUndoRedo(v);
  const onSave = () => dataToSave && placeStore?.save(dataToSave);
  const onReload = () => placeStore?.load();

  useEffect(() => {
    placeStore?.setEditorLock(true);
  }, [placeStore]);

  useEffect(() => {
    if (!props.schemeDragActive || !locked) {
      setDropLockedHintStyle(undefined);
      return;
    }
    if (dropLockedHintStyle || !editorRef.current || !dragStore?.mousePos || !dragStore.shift) return;

    const point = {
      x: dragStore.mousePos.x + dragStore.shift.x,
      y: dragStore.mousePos.y + dragStore.shift.y,
    };
    const rect = editorRef.current.getBoundingClientRect();
    const isInside = point.x > rect.left && point.x < rect.right && point.y > rect.top && point.y < rect.bottom;
    if (!isInside) return;

    setDropLockedHintStyle({
      left: Math.min(point.x + 18, window.innerWidth - 300),
      top: Math.min(point.y + 18, window.innerHeight - 120),
    });
  }, [dragStore?.mousePos, dragStore?.shift, dropLockedHintStyle, editorRef, locked, props.schemeDragActive]);

  useEffect(() => {
    if (!props.focusNodeID || props.kind !== EQUIPMENT_TYPE.ELECTRICAL) return;

    const node = nodes?.find((n) => n.id === props.focusNodeID);
    if (!node) return;

    reactFlow.setCenter(node.position.x, node.position.y, {
      zoom: Math.max(internalViewport.zoom, 1),
      duration: 400,
    });
    props.onFocusHandled?.(props.focusNodeID);
  }, [internalViewport.zoom, nodes, props.focusNodeID, props.kind, props.onFocusHandled, reactFlow]);
  
  return {
    nodes,
    renderedNodes,
    edges,
    saved,
    loading,
    hasUndo: undoLength > 0,
    hasRedo: redoLength > 0,
    isEmpty: !nodes.length,
    editorRef,
    locked,
    dropLockedHintStyle,
    savedViewport: isInitialViewport ? savedViewport : undefined,

    setLocked(locked: boolean) { placeStore?.setEditorLock(locked); },
    onNodesChange,
    onEdgesChange,
    onClear,
    onDragOver,
    onDragStart,
    onDrop,
    onConnect,
    onUndoRedo,
    onSave,
    onReload,
  };
}

export interface NetworkEditorProps3 {
  placeID: PlaceID3;
  kind: EQUIPMENT_TYPE;
  editorRef: MutableRefObject<HTMLDivElement | null>;
  schemeDragActive?: boolean;
  focusNodeID?: string;
  highlightNodeIDs?: string[];
  onFocusHandled?: (nodeID: string) => void;
}

const NetworkEditor3: React.FC<NetworkEditorProps3> = observer((props: NetworkEditorProps3) => {
  const ctrl = useControl(props);
  const { formatMessage } = useIntl();

  return (
    <div className={cn(styles.electricNetwork, {[styles.inactive]: ctrl.loading})}>
      <div className={cn(styles.topPanel, {[styles.inactive]: ctrl.locked})}>
        <div className={cn(styles.palette)}>
          { nodesDescriptions3.filter(descr => descr.kind === props.kind /*&& descr.type !== "building"*/).map(descr =>
            <button 
              key={`${descr.type}`} 
              className={styles.paletteItem} 
              draggable
              style={{backgroundColor: descr.color}} 
              onDragStart={(event) => ctrl.onDragStart(event, descr.type)}
            >
              { descr.kind === EQUIPMENT_TYPE.NETWORK 
                ? <Figure3 icon={descr.icon} figure={descr.type as NodeFigure3} baseColor={descr.color}/>
                : <Icon src={descr.icon}/>
              }
              { descr.kind === EQUIPMENT_TYPE.NETWORK 
                ? ""
                : formatMessage({id: `location.diagram.${descr.type}_element`})
              }
            </button>
          )}
        </div>
        <div className={styles.actions}>
          <div className={styles.refresh} title={formatMessage({ id: "location.diagram.refresh"})}>
            <RefreshRacks3 variant="editor" />
          </div>
          <button
            className={cn(styles.clearField, {[styles.inactive]: ctrl.isEmpty})}
            onClick={ctrl.onClear}
          >
            {formatMessage({ id: "location.diagram.clear_field"})}
          </button>
        </div>
      </div>
      <div
        className={cn(styles.editor, {
          [styles.dropLocked]: ctrl.locked && props.schemeDragActive,
        })}
        onDrop={ctrl.onDrop}
        onDragOver={ctrl.onDragOver}
        ref={ctrl.editorRef}
      >
          {ctrl.locked && props.schemeDragActive && ctrl.dropLockedHintStyle && (
            <div className={styles.dropLockedOverlay} style={ctrl.dropLockedHintStyle}>
              {formatMessage({ id: "location.diagram.unlock_to_drop" })}
            </div>
          )}
          <ReactFlow
            nodes={ctrl.renderedNodes}
            edges={ctrl.edges?.map(e => toJS(e))}
            style={WLP202603 ? { background: "#F7F3FC" } : undefined}
            onConnect={ctrl.onConnect}
            onNodesChange={ctrl.onNodesChange}
            onEdgesChange={ctrl.onEdgesChange}
            nodeTypes={NodeTypes}
            defaultEdgeOptions={{type: "smoothstep", style: {strokeWidth: "2px"}}}
            defaultViewport={ctrl.savedViewport}
            nodesDraggable={!ctrl.locked}
            nodesConnectable={!ctrl.locked}
            elementsSelectable={!ctrl.locked}
            selectNodesOnDrag={!ctrl.locked}
            connectOnClick={!ctrl.locked}
            panOnDrag={ctrl.locked}
            noPanClassName="nopan"
            noDragClassName="nodrag"
            //fitView
          >
            <Controls showInteractive={false} className={styles.flowControls}>
              <ControlButton
                className={cn(styles.lockButton, { [styles.locked]: ctrl.locked })}
                onClick={() => ctrl.setLocked(!ctrl.locked)}
                title={ctrl.locked ? "Unlock editor" : "Lock editor"}
              >
                <Icon src={ctrl.locked ? lockIcon : unlockIcon} noHover />
              </ControlButton>
            </Controls>
            <MiniMap style={WLP202603 ? { background: "#F3EFF8" } : undefined} />
            <Background />
          </ReactFlow>
      </div>
      <div className={cn(styles.bottomPanel, {[styles.inactive]: ctrl.locked})}>
        <button
          className={cn(styles.button, {[styles.inactive]: !ctrl.hasUndo})} 
          onClick={() => ctrl.onUndoRedo(true)}
        >
          <Icon src={undoIcon}/>
          {formatMessage({ id: "location.diagram.undo"})}
        </button>
        <button 
          className={cn(styles.button, {[styles.inactive]: !ctrl.hasRedo})} 
          onClick={() => ctrl.onUndoRedo(false)}
        >
          <Icon src={redoIcon}/>
          {formatMessage({ id: "location.diagram.redo"})}
        </button>
        <button 
          className={cn(styles.button, {[styles.inactive]: ctrl.saved})} 
          onClick={() => ctrl.onSave()}
        >
          <img src={saveIcon} alt="" />
          {formatMessage({ id: "location.diagram.save"})}
        </button>
        { SHOW_ELECTRIC_RELOAD_BUTTON && 
          <button 
            className={cn(styles.button, {[styles.inactive]: !ctrl.hasUndo})} 
            onClick={() => ctrl.onReload()}
          >
            {formatMessage({ id: "location.diagram.reload"})}
          </button>
        }
      </div>
    </div>
  );
});

NetworkEditor3.displayName = "NetworkEditor3";

export default NetworkEditor3;
